import { v4 as uuidv4 } from 'uuid';

// Initialize dataLayer if it doesn't exist
const initializeDataLayer = () => {
  if (typeof window !== 'undefined' && !window.dataLayer) {
    window.dataLayer = window.dataLayer || [];
  }
};

// Check if we're in browser environment
const isBrowser = () => typeof window !== 'undefined';

// Get attribution data from localStorage with optional filtering
export const getAttributionData = (requiredParams = null) => {
  if (!isBrowser()) return {};
  
  const allAttributionKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'ref'];
  const keysToCheck = requiredParams || allAttributionKeys;
  
  return keysToCheck.reduce((acc, key) => {
    const value = localStorage.getItem(key);
    if (value) acc[key] = value;
    return acc;
  }, {});
};

// Clean object by removing null/undefined values
const cleanEventData = (data) => {
  return Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== null && value !== undefined)
  );
};

// Base event pusher
export const gtmPushEvent = (event) => {
  if (!isBrowser() || !window.dataLayer) {
    console.warn('GTM: dataLayer not available');
    return;
  }
  
  const cleanEvent = cleanEventData(event);
  window.dataLayer.push(cleanEvent);
  
  console.log('GTM Event pushed:', cleanEvent);
};

// Create base event with common properties
const createBaseEvent = (eventName, additionalData = {}) => ({
  event: eventName,
  timestamp: new Date().toISOString(),
  session_id: sessionStorage.getItem('session_id') || uuidv4(),
  ...additionalData
});

// Track page views - gets all attribution data
export const gtmPageView = (additionalData = {}) => {
  if (!isBrowser() || !window.dataLayer) {
    console.warn('GTM: dataLayer not available');
    return;
  }
  
  const eventData = createBaseEvent('page_view', {
    ...additionalData,
    ...getAttributionData()
  });
  
  gtmPushEvent(eventData);
};

// Track user login
export const gtmTrackLogin = (user_id, method = 'email') => {
  const eventData = createBaseEvent('login', {
    method,
    user_id,
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  gtmPushEvent(eventData);
};

// Track user registration
export const gtmTrackRegistration = (user_id, method = 'email') => {
  const eventData = createBaseEvent('sign_up', {
    method,
    user_id,
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  gtmPushEvent(eventData);
};

// Track form submissions
export const gtmTrackFormSubmit = (form_name, form_step = null) => {
  const eventData = createBaseEvent('form_submit', {
    form_name,
    form_step,
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  gtmPushEvent(eventData);
};

// Track button clicks
export const gtmTrackButtonClick = (button_name, section = null) => {
  const eventData = createBaseEvent('click', {
    button_name,
    section
  });
  
  gtmPushEvent(eventData);
};

// Generic event tracker for custom events
export const gtmTrackCustomEvent = (eventName, eventData = {}) => {
  const customEventData = createBaseEvent(eventName, eventData);
  gtmPushEvent(customEventData);
};

/**
 * Read a cookie value by name (browser only). Returns null if absent.
 */
export const readCookie = (name) => {
  if (!isBrowser()) return null;
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * getConsentContext — returns the ad consent snapshot as a plain object.
 * Source priority: `_tkg_consent` cookie (mirrored cross-subdomain from www)
 * → localStorage.consentMode. Returns null if neither is present/parseable.
 *
 * The API persists this JSON and derives ad_user_data_consent /
 * ad_personalization_consent from it (defaulting to "denied" when absent).
 */
export const getConsentContext = () => {
  if (!isBrowser()) return null;
  const raw = readCookie('_tkg_consent') || localStorage.getItem('consentMode');
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
};

/**
 * getOpenAiAttribution — read the OpenAI/ChatGPT ad attribution cookies
 * (`__oppref`, `__obref`) set by the OpenAI Pixel. These already persist
 * cross-subdomain (.tokenizedgreen.es), so no mirroring is needed.
 *
 * Returns an object containing ONLY the keys that are actually present, e.g.
 * { oppref: '...', obref: '...' } — or {} if the visitor did not come from an
 * OpenAI ad. Callers should spread the result so absent keys are simply omitted.
 */
export const getOpenAiAttribution = () => {
  if (!isBrowser()) return {};
  const result = {};
  const oppref = readCookie('__oppref');
  const obref = readCookie('__obref');
  if (oppref) result.oppref = oppref;
  if (obref) result.obref = obref;
  return result;
};

// Consent categories we track. Google Consent Mode v2 naming.
export const CONSENT_KEYS = ['ad_storage', 'ad_user_data', 'ad_personalization', 'analytics_storage'];

/**
 * hasConsentDecision — true once the user has made ANY choice (accept/reject),
 * i.e. a consentMode snapshot exists. Used to decide whether to show the banner.
 */
export const hasConsentDecision = () => {
  return !!getConsentContext();
};

/**
 * setConsent — persist the user's consent decision.
 * Writes the snapshot to BOTH localStorage.consentMode and the shared
 * `_tkg_consent` cookie (domain .tokenizedgreen.es) so the app, the API, and
 * www all read the same values.
 *
 * @param {Object} decision map of consent keys to 'granted' | 'denied'.
 *        Missing keys default to 'denied'.
 */
export const setConsent = (decision = {}) => {
  if (!isBrowser()) return null;

  const snapshot = CONSENT_KEYS.reduce((acc, key) => {
    acc[key] = decision[key] === 'granted' ? 'granted' : 'denied';
    return acc;
  }, {});

  const json = JSON.stringify(snapshot);
  try {
    localStorage.setItem('consentMode', json);
    const expires = new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie =
      '_tkg_consent=' + encodeURIComponent(json) +
      ';domain=.tokenizedgreen.es;path=/;expires=' + expires + ';SameSite=Lax';
  } catch (e) {
    /* storage may be unavailable (private mode); fail silently */
  }

  // Mirror into Google Consent Mode if gtag is present.
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('consent', 'update', snapshot);
    }
  } catch (e) { /* noop */ }

  return snapshot;
};

/**
 * acceptAllConsent / rejectAllConsent — convenience helpers for the banner.
 */
export const acceptAllConsent = () =>
  setConsent(CONSENT_KEYS.reduce((a, k) => ({ ...a, [k]: 'granted' }), {}));

export const rejectAllConsent = () =>
  setConsent(CONSENT_KEYS.reduce((a, k) => ({ ...a, [k]: 'denied' }), {}));

// Event used to re-open the consent preferences from anywhere (e.g. a footer
// "Cookie settings" link) without prop-drilling into the banner component.
export const OPEN_COOKIE_SETTINGS_EVENT = 'tkg:open-cookie-settings';

/**
 * openCookieSettings — dispatch the event that re-opens the consent banner's
 * preferences panel. Call this from any "Cookie settings" control.
 */
export const openCookieSettings = () => {
  if (!isBrowser()) return;
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
};

/**
 * captureAttributionParams — call once on app mount.
 * Reads gclid, fbclid, UTMs, and ref from the current URL and persists them
 * in localStorage so they survive navigation within the SPA and are available
 * at signup form submission even if the URL has changed by then.
 *
 * Only overwrites a key if the URL actually contains it (last-touch model).
 * Also stores first_touch_url / first_touch_at on very first visit.
 */
export const captureAttributionParams = () => {
  if (!isBrowser()) return;

  const urlParams = new URLSearchParams(window.location.search);
  const attributionKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'ref'];

  attributionKeys.forEach(key => {
    const value = urlParams.get(key);
    if (value) {
      localStorage.setItem(key, value);
    }
  });

  // Store first-touch data only once (never overwrite)
  if (!localStorage.getItem('first_touch_at')) {
    localStorage.setItem('first_touch_url', document.referrer || 'direct');
    localStorage.setItem('first_touch_at', new Date().toISOString());
    localStorage.setItem('landing_url', window.location.href);
    localStorage.setItem('first_site_landing_url', window.location.href);
  }

  // Ensure session ID exists
  if (!sessionStorage.getItem('session_id')) {
    sessionStorage.setItem('session_id', Date.now() + '_' + Math.random().toString(36).substr(2, 9));
  }
};

// Initialize GTM on module load
initializeDataLayer();